import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, X, Check, CaretDown, CaretUp } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABELS = {
  'weight-loss': { label: 'Weight Loss', emoji: '🔥' },
  'muscle-gain': { label: 'Muscle Gain', emoji: '💪' },
  'lunch':       { label: 'Lunch',        emoji: '🍱' },
  'dinner':      { label: 'Dinner',       emoji: '🌙' },
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export function AdminPlansEditorTab() {
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState('weight-loss');
  const [editingPlanMeta, setEditingPlanMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({});
  const [editingMeal, setEditingMeal] = useState(null); // day string
  const [mealForm, setMealForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/fitness-subscription-plans`, { withCredentials: true });
      setPlans(data);
    } catch { toast.error('Failed to load plans'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentPlan = plans.find(p => p.plan_type === activePlan);

  const startEditMeta = () => {
    setMetaForm({
      title: currentPlan.title,
      subtitle: currentPlan.subtitle,
      weekly_price: currentPlan.weekly_price,
      monthly_price: currentPlan.monthly_price,
    });
    setEditingPlanMeta(true);
    setEditingMeal(null);
  };

  const saveMeta = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/fitness-subscription-plans/${activePlan}`, {
        ...metaForm,
        weekly_price: Number(metaForm.weekly_price),
        monthly_price: Number(metaForm.monthly_price),
      }, { withCredentials: true });
      toast.success('Plan updated');
      setEditingPlanMeta(false);
      load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const startEditMeal = (meal) => {
    setMealForm({
      name: meal.name,
      description: meal.description || '',
      options: (meal.options || []).join(', '),
      calories: meal.calories || '',
      protein: meal.protein || '',
      fibre: meal.fibre || '',
    });
    setEditingMeal(meal.day);
    setEditingPlanMeta(false);
  };

  const saveMeal = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/fitness-subscription-plans/${activePlan}/meals/${editingMeal}`,
        mealForm,
        { withCredentials: true }
      );
      toast.success(`${editingMeal} meal updated`);
      setEditingMeal(null);
      load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (!plans.length) return (
    <div className="flex items-center justify-center h-40">
      <div className="w-8 h-8 border-4 border-[#1A4331] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="plans-editor-tab">
      {/* Plan type selector */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {plans.map(p => {
          const lbl = PLAN_LABELS[p.plan_type] || { label: p.plan_type, emoji: '📋' };
          return (
            <button key={p.plan_type}
              onClick={() => { setActivePlan(p.plan_type); setEditingMeal(null); setEditingPlanMeta(false); }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activePlan === p.plan_type ? 'bg-[#1A4331] text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
              data-testid={`plan-tab-${p.plan_type}`}
            >
              {lbl.emoji} {lbl.label}
            </button>
          );
        })}
      </div>

      {currentPlan && (
        <div className="space-y-5">
          {/* Plan metadata card */}
          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-[#1C1917] text-lg">{currentPlan.title}</h3>
                <p className="text-sm text-stone-500 mt-0.5 max-w-lg">{currentPlan.subtitle}</p>
              </div>
              {!editingPlanMeta && (
                <button onClick={startEditMeta}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl bg-stone-100 text-stone-700 hover:bg-stone-200 transition"
                  data-testid="edit-plan-meta-btn">
                  <Pencil size={14} /> Edit
                </button>
              )}
            </div>

            {editingPlanMeta ? (
              <div className="space-y-3 bg-stone-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-xs text-stone-500 font-medium block mb-1">Plan Title</label>
                    <input value={metaForm.title} onChange={e => setMetaForm(p => ({...p, title: e.target.value}))}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-stone-500 font-medium block mb-1">Subtitle / Description</label>
                    <textarea value={metaForm.subtitle} onChange={e => setMetaForm(p => ({...p, subtitle: e.target.value}))} rows={2}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331] resize-none" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 font-medium block mb-1">Weekly Price (₹)</label>
                    <input type="number" value={metaForm.weekly_price} onChange={e => setMetaForm(p => ({...p, weekly_price: e.target.value}))}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 font-medium block mb-1">Monthly Price (₹)</label>
                    <input type="number" value={metaForm.monthly_price} onChange={e => setMetaForm(p => ({...p, monthly_price: e.target.value}))}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]" />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={saveMeta} disabled={saving}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#1A4331] text-white rounded-xl text-sm font-medium disabled:opacity-50">
                    <Check size={14} /> {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button onClick={() => setEditingPlanMeta(false)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-stone-100 text-stone-700 rounded-xl text-sm">
                    <X size={14} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-4">
                <div className="bg-stone-50 rounded-xl px-4 py-3 text-center">
                  <p className="text-xs text-stone-500 font-medium">Weekly</p>
                  <p className="text-xl font-bold text-[#1A4331]">₹{currentPlan.weekly_price}</p>
                </div>
                <div className="bg-stone-50 rounded-xl px-4 py-3 text-center">
                  <p className="text-xs text-stone-500 font-medium">Monthly</p>
                  <p className="text-xl font-bold text-[#1A4331]">₹{currentPlan.monthly_price}</p>
                </div>
              </div>
            )}
          </div>

          {/* Weekly meals */}
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100">
              <h4 className="font-semibold text-[#1C1917]">Weekly Menu (Mon – Fri)</h4>
              <p className="text-xs text-stone-400 mt-0.5">Click the edit icon on any day to update the meal</p>
            </div>
            <div className="divide-y divide-stone-100">
              {(currentPlan.meals || []).map(meal => (
                <div key={meal.day} className="px-5 py-4">
                  {editingMeal === meal.day ? (
                    <div className="bg-stone-50 rounded-xl p-4 space-y-3">
                      <p className="text-xs font-bold text-[#1A4331] uppercase tracking-wide">{meal.day}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="text-xs text-stone-500 font-medium block mb-1">Meal Name</label>
                          <input value={mealForm.name} onChange={e => setMealForm(p => ({...p, name: e.target.value}))}
                            className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-stone-500 font-medium block mb-1">Description</label>
                          <input value={mealForm.description} onChange={e => setMealForm(p => ({...p, description: e.target.value}))}
                            className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
                            placeholder="e.g. 3 pieces · Curd" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-xs text-stone-500 font-medium block mb-1">Options (comma-separated)</label>
                          <input value={mealForm.options} onChange={e => setMealForm(p => ({...p, options: e.target.value}))}
                            className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
                            placeholder="Paneer Bowl, Chicken Bowl" />
                        </div>
                        {['calories', 'protein', 'fibre'].map(field => (
                          <div key={field}>
                            <label className="text-xs text-stone-500 font-medium block mb-1 capitalize">{field}{field === 'protein' || field === 'fibre' ? ' (g)' : ' (kcal)'}</label>
                            <input type="number" value={mealForm[field]} onChange={e => setMealForm(p => ({...p, [field]: e.target.value}))}
                              className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]" />
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button onClick={saveMeal} disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 bg-[#1A4331] text-white rounded-xl text-sm font-medium disabled:opacity-50">
                          <Check size={14} /> {saving ? 'Saving…' : 'Save Meal'}
                        </button>
                        <button onClick={() => setEditingMeal(null)}
                          className="flex items-center gap-1.5 px-4 py-2 bg-stone-100 text-stone-700 rounded-xl text-sm">
                          <X size={14} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <span className="w-20 flex-shrink-0 text-xs font-bold text-stone-400 uppercase pt-0.5">{meal.day.slice(0,3)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1C1917]">{meal.name}</p>
                        {meal.description && <p className="text-xs text-stone-400 mt-0.5">{meal.description}</p>}
                        {meal.options?.length > 0 && (
                          <div className="flex gap-1.5 mt-1.5 flex-wrap">
                            {meal.options.map(o => (
                              <span key={o} className="text-[11px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">{o}</span>
                            ))}
                          </div>
                        )}
                        {meal.calories > 0 && (
                          <p className="text-xs text-stone-400 mt-1">{meal.calories} kcal · {meal.protein}g protein</p>
                        )}
                      </div>
                      <button onClick={() => startEditMeal(meal)}
                        className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-[#1A4331] transition flex-shrink-0"
                        data-testid={`edit-meal-${meal.day}`}>
                        <Pencil size={15} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPlansEditorTab;
